import moment from 'moment';
import { useFilterSearchParams } from 'pages/Shipments/Containers/utilities';
import { DEFAULT_DATE_RANGE_PARAM } from 'pages/Shipments/params';
import * as React from 'react';
import { Checkbox } from 'components/tailwind-ui/Checkbox';
import { Section } from './Section';
import { DateRangeOptions, Date } from './DateRangeOptions';

interface FilterStates {
  [key: string]: boolean | Date;
  today: boolean;
  tomorrow: boolean;
  exactDate: Date;
  startDate: Date;
  endDate: Date;
}

const defaultFilterStates: FilterStates = {
  today: false,
  tomorrow: false,
  exactDate: null,
  startDate: null,
  endDate: null,
};

export const LastFreeDay: React.FC = () => {
  const { searchParams, setSearchParams } = useFilterSearchParams(true);

  const filterStates: FilterStates = React.useMemo(() => {
    const { lastFreeDayAt } = searchParams;

    if (!lastFreeDayAt) {
      return { ...defaultFilterStates };
    }

    return {
      today: lastFreeDayAt.exactDate === 'today' || lastFreeDayAt.startDate === 'today',
      tomorrow: lastFreeDayAt.exactDate === '+1:days' || lastFreeDayAt.endDate === '+1:days',
      exactDate:
        lastFreeDayAt.exactDate && !['today', 'tomorrow'].includes(lastFreeDayAt.exactDate)
          ? moment(lastFreeDayAt.exactDate)
          : null,
      startDate:
        (lastFreeDayAt.startDate &&
          !['today', 'tomorrow'].includes(lastFreeDayAt.startDate) &&
          moment(lastFreeDayAt.startDate)) ||
        null,
      endDate:
        (lastFreeDayAt.endDate &&
          !['today', 'tomorrow'].includes(lastFreeDayAt.endDate) &&
          moment(lastFreeDayAt.endDate)) ||
        null,
    };
  }, [searchParams]);

  const clearFilters = React.useCallback(() => {
    setSearchParams({
      lastFreeDayAt: DEFAULT_DATE_RANGE_PARAM,
    });
  }, [setSearchParams]);

  const handleChange = React.useCallback(
    (v: string) => {
      const newFilterState = filterStates;

      newFilterState[v] = !newFilterState[v];

      if (newFilterState.today && newFilterState.tomorrow) {
        setSearchParams({
          lastFreeDayAt: {
            ...DEFAULT_DATE_RANGE_PARAM,
            startDate: 'today',
            endDate: '+1:days',
          },
        });
      } else if (newFilterState.today) {
        setSearchParams({
          lastFreeDayAt: {
            ...DEFAULT_DATE_RANGE_PARAM,
            exactDate: 'today',
          },
        });
      } else if (newFilterState.tomorrow) {
        setSearchParams({
          lastFreeDayAt: {
            ...DEFAULT_DATE_RANGE_PARAM,
            exactDate: '+1:days',
          },
        });
      } else {
        clearFilters();
      }
    },
    [setSearchParams, filterStates, clearFilters]
  );

  return (
    <Section title="Demurrage last free day filters">
      <div className="flex">
        <Checkbox
          id="demurrage-last-free-day-today"
          key="today"
          label="Today"
          checked={filterStates.today}
          onCheck={() => handleChange('today')}
          className="mr-8"
        />
        <Checkbox
          id="demurrage-last-free-day-tomorrow"
          key="tomorrow"
          label="Tomorrow"
          checked={filterStates.tomorrow}
          onCheck={() => handleChange('tomorrow')}
          className="mr-8"
        />
        <DateRangeOptions filterStates={filterStates} field="lastFreeDayAt" />
      </div>
    </Section>
  );
};
LastFreeDay.displayName = 'Filters.More.LastFreeDay';
